import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type TextareaHTMLAttributes } from "react";
import {
  Activity, BookOpen, Bot, Brain, Building2, FileSearch, FlaskConical, Gauge, Glasses, Globe, LineChart, ListChecks, Mail, Megaphone, Network, Newspaper,
  PenLine, PieChart, Puzzle, Radar, Scale, ScanSearch, ScrollText, ShieldCheck, Sparkles, Swords, Target, Timer, TrendingUp, Users, Zap, EyeOff, Landmark,
  type LucideIcon,
} from "lucide-react";
import { useLocale } from "@ncp/i18n";
import { arabicTerms, stripDemoTags } from "@ncp/ui";
import { pick, type Bi, type Mission, type MissionState, type Tone } from "../mission/engine";

export const ICONS: Record<string, LucideIcon> = {
  "file-search": FileSearch, scale: Scale, gauge: Gauge, "list-checks": ListChecks, building: Building2, "shield-check": ShieldCheck, radar: Radar,
  activity: Activity, megaphone: Megaphone, "eye-off": EyeOff, users: Users, timer: Timer, target: Target, "pie-chart": PieChart, "scan-search": ScanSearch,
  "line-chart": LineChart, "trending-up": TrendingUp, flask: FlaskConical, network: Network, "book-open": BookOpen, newspaper: Newspaper, swords: Swords,
  pen: PenLine, glasses: Glasses, zap: Zap, puzzle: Puzzle, scroll: ScrollText, mail: Mail, globe: Globe, bot: Bot, sparkles: Sparkles, landmark: Landmark, brain: Brain,
};
export const iconOf = (key: string): LucideIcon => ICONS[key] ?? Bot;

export const TONE_COLOR: Record<Tone, string> = { ok: "var(--agx-green)", info: "var(--agx-cyan)", warn: "var(--agx-amber)", risk: "var(--agx-rose)" };

/** The mission's state, re-rendered once per animation frame at most. */
export function useMission(mission: Mission): MissionState {
  return useSyncExternalStore(mission.subscribe, mission.getSnapshot, mission.getSnapshot);
}

/**
 * Text in the officer's language. On an Arabic screen it reads as the rest of the back office does (V1, V4): economic abbreviations as their
 * Arabic terms (HHI → مؤشر هيرفندال-هيرشمان, CR4, GUPPI, UPP, SSNIP …) and no demonstration tags; English case text that remains is
 * translated by the page's content-language layer (the page is a <main>, like the staff shell's).
 */
export function useT(): (text: Bi | string | null | undefined) => string {
  const { locale } = useLocale();
  return (text) => {
    const value = pick(text, locale);
    return locale === "ar" && value ? arabicTerms(stripDemoTags(value)) : value;
  };
}

/** Types a text out over `ms` (the agent's own pace); a text that is not the newest shows whole. */
export function Typewriter({ text, ms, active = true, className }: { text: string; ms: number; active?: boolean; className?: string }) {
  const [shown, setShown] = useState(active ? 0 : text.length);
  const started = useRef<number | null>(null);
  useEffect(() => {
    if (!active) { setShown(text.length); return; }
    started.current = performance.now();
    setShown(0);
    let frame = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - (started.current ?? now)) / Math.max(1, ms));
      setShown(Math.round(p * text.length));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [text, ms, active]);
  const typing = active && shown < text.length;
  // Half-typed words are not sent for translation; the whole text is, once typed.
  return <span className={`${className ?? ""} ${typing ? "agx-caret" : ""}`} data-no-translate={typing ? "" : undefined}>{text.slice(0, shown)}</span>;
}

/** Elapsed time of an agent or the mission, ticking while it runs. */
export function Elapsed({ from, to, className }: { from?: number; to?: number; className?: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!from || to) return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [from, to]);
  if (!from) return <span className={className}>0:00</span>;
  const s = Math.max(0, Math.floor(((to ?? now) - from) / 1000));
  return <span className={`agx-mono ${className ?? ""}`}>{Math.floor(s / 60)}:{String(s % 60).padStart(2, "0")}</span>;
}

/** A number that rolls to its new value. */
export function Counter({ value, className }: { value: number; className?: string }) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const start = performance.now();
    const a = from.current;
    let frame = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / 450);
      setShown(Math.round(a + (value - a) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) frame = requestAnimationFrame(tick);
      else from.current = value;
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  return <span className={`agx-mono ${className ?? ""}`}>{shown.toLocaleString("en")}</span>;
}

export type AgxTheme = "dark" | "light";
const THEME_KEY = "ncp.agentic.theme";

/** The officer's theme for the agentic review, remembered in this browser (a convenience: it falls back to dark when storage is blocked). */
export function useAgxTheme(): [AgxTheme, () => void] {
  const [theme, setTheme] = useState<AgxTheme>(() => {
    try { return window.localStorage.getItem(THEME_KEY) === "light" ? "light" : "dark"; } catch { return "dark"; }
  });
  const toggle = () => setTheme((t) => {
    const next: AgxTheme = t === "dark" ? "light" : "dark";
    try { window.localStorage.setItem(THEME_KEY, next); } catch { /* storage blocked: the choice lasts for this visit */ }
    return next;
  });
  return [theme, toggle];
}

export const compact = (n: number) => (n >= 10_000 ? `${(n / 1000).toFixed(1)}k` : n.toLocaleString("en"));

/** A question field that grows with what is typed, up to `max` pixels, then scrolls. Enter sends; Shift+Enter starts a new line. */
export function GrowingInput({ value, onChange, onSend, max = 132, className, ...props }: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> & {
  value: string; onChange: (value: string) => void; onSend: () => void; max?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const fit = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  };
  useLayoutEffect(fit, [value, max]);
  useEffect(() => {
    // A box opened to full size wraps the same words differently.
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let width = el.clientWidth;
    const watch = new ResizeObserver(() => { if (el.clientWidth !== width) { width = el.clientWidth; fit(); } });
    watch.observe(el);
    return () => watch.disconnect();
  }, [max]);
  return (
    <textarea ref={ref} rows={1} dir="auto" value={value} onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); onSend(); } }}
      className={`block resize-none bg-transparent outline-none focus-visible:ring-0 focus-visible:ring-offset-0 ${className ?? ""}`} {...props} />
  );
}
